import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { NEWS } from "@/constants/testIds";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import PromoCountdown from "@/components/PromoCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "Tout", testId: NEWS.filterAll },
  { key: "actualite", label: "Actualités du secteur", testId: NEWS.filterActualite },
  { key: "promotion", label: "Promotions", testId: NEWS.filterPromotion },
  { key: "avantage", label: "Avantages clients", testId: NEWS.filterAvantage },
];

const CATEGORY_LABEL = {
  actualite: "Actualité",
  promotion: "Promotion",
  avantage: "Avantage",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function NewsListPage() {
  const [filter, setFilter] = useState("all");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/articles`)
      .then((res) => {
        if (!cancelled) setArticles(res.data);
      })
      .catch(() => {
        if (!cancelled) setError("Impossible de charger les actualités.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return articles;
    return articles.filter((a) => a.category === filter);
  }, [articles, filter]);

  return (
    <section data-testid={NEWS.root} className="bg-[#f7f5f0]">
      {/* Hero */}
      <div className="relative pt-36 pb-16 md:pt-44 md:pb-20 border-b border-[#0a192f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#c05c3d]" />
            <span className="overline">Journal du cabinet</span>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#0a192f] max-w-4xl">
            Actualités,<br />
            <span className="italic text-[#c05c3d]">promotions</span> &
            avantages.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#475569] leading-relaxed">
            Notre lecture du marché de l&apos;assurance et de la gestion de
            patrimoine. Décryptages réglementaires, offres réservées à nos
            clients et engagements de méthode.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-20 z-30 bg-[#f7f5f0]/85 backdrop-blur-xl border-b border-[#0a192f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={f.testId}
              onClick={() => setFilter(f.key)}
              className={`px-5 py-2 text-xs tracking-[0.18em] uppercase font-medium transition-colors duration-300 ${
                filter === f.key
                  ? "bg-[#0a192f] text-[#efeade]"
                  : "border border-[#0a192f]/20 text-[#0a192f] hover:bg-[#0a192f] hover:text-[#efeade]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        {loading && (
          <div className="text-[#475569] text-sm">Chargement des articles…</div>
        )}
        {error && <div className="text-[#c05c3d] text-sm">{error}</div>}
        {!loading && !error && visible.length === 0 && (
          <div className="text-[#475569] text-sm">
            Aucun article dans cette catégorie pour l&apos;instant.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visible.map((a, i) => (
            <NewsCard key={a.id} article={a} featured={i === 0 && filter === "all"} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsCard({ article }) {
  const badgeColor =
    article.category === "promotion"
      ? "bg-[#c05c3d] text-[#efeade]"
      : article.category === "avantage"
      ? "bg-[#b08d57] text-[#0a192f]"
      : "bg-[#0a192f] text-[#efeade]";

  return (
    <Link
      to={`/actualites/${article.slug}`}
      data-testid={NEWS.card(article.slug)}
      className="group relative flex flex-col bg-white border border-[#0a192f]/10 hover:-translate-y-1 transition-transform duration-500"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.image_url}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <span
          className={`absolute top-4 left-4 text-[10px] tracking-[0.24em] uppercase font-semibold px-3 py-1.5 ${badgeColor}`}
        >
          {CATEGORY_LABEL[article.category] || article.category}
        </span>
      </div>
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <div className="overline mb-3">{article.tag}</div>
        <h3 className="font-serif text-2xl md:text-[26px] leading-tight text-[#0a192f] group-hover:text-[#c05c3d] transition-colors">
          {article.title}
        </h3>
        <p className="mt-3 text-[#475569] text-[15px] leading-relaxed flex-1">
          {article.excerpt}
        </p>
        {article.category === "promotion" && article.promo_ends_at && (
          <div className="mt-4">
            <PromoCountdown endsAt={article.promo_ends_at} />
          </div>
        )}
        <div className="mt-6 flex items-center justify-between text-xs text-[#475569]">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} strokeWidth={1.5} />
              {formatDate(article.published_at)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} strokeWidth={1.5} />
              {article.read_minutes} min
            </span>
          </div>
          <ArrowUpRight
            size={18}
            strokeWidth={1.5}
            className="text-[#c05c3d] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
          />
        </div>
      </div>
    </Link>
  );
}
